/**
 * Replay buffer — a fixed-capacity ring of WorldSnapshots.
 *
 * Every slot is a preallocated snapshot object; `push` copies field-by-field
 * into the slot (no allocation in steady state), so recording 30Hz for 90s
 * creates zero GC churn after warm-up.
 */
import type { PlayerState, Vec3, WorldSnapshot } from '../core/contracts';

export interface ReplaySample {
  prev: WorldSnapshot;
  next: WorldSnapshot;
  /** 0..1 blend from prev to next. */
  alpha: number;
}

export interface ReplayBufferOptions {
  /** Seconds of history to keep (default 90). */
  seconds?: number;
  /** Recording rate (default 30Hz). Pushes closer together than 1/hz are skipped. */
  hz?: number;
}

export interface ReplayBuffer {
  readonly capacity: number;
  readonly size: number;
  /** Sim time of the oldest / newest stored snapshot (NaN when empty). */
  readonly oldestT: number;
  readonly newestT: number;
  /** Record a snapshot (deep-copied). Returns false if skipped by rate limiting. */
  push(s: WorldSnapshot): boolean;
  /** Bracketing snapshots for time t (clamped to the stored range). Result object is reused. */
  sample(t: number): ReplaySample | null;
  /** Write an interpolated snapshot for time t into `out` (allocate once with createEmptySnapshot). */
  interpolate(t: number, out: WorldSnapshot): WorldSnapshot | null;
  /** i-th stored snapshot, 0 = oldest. Do not retain/mutate. */
  at(i: number): WorldSnapshot;
  clear(): void;
}

const vec = (): Vec3 => ({ x: 0, y: 0, z: 0 });

function emptyPlayer(): PlayerState {
  return { id: '', team: 'home', number: 0, pos: vec(), vel: vec(), heading: 0, anim: 'idle', animPhase: 0, hasBall: false };
}

export function createEmptySnapshot(playerCount = 22): WorldSnapshot {
  const players: PlayerState[] = [];
  for (let i = 0; i < playerCount; i++) players.push(emptyPlayer());
  return {
    t: 0, matchClock: 0, half: 1, phase: 'pre_match',
    score: { home: 0, away: 0 }, possession: null,
    ball: { pos: vec(), vel: vec(), spin: 0, ownerId: null },
    players,
    officials: { referee: vec(), assistant1: vec(), assistant2: vec() },
    focus: vec(), intensity: 0,
  };
}

const cp = (d: Vec3, s: Vec3): void => { d.x = s.x; d.y = s.y; d.z = s.z; };

/** Fast deep copy of `src` into the preallocated `dst`. Returns dst. */
export function copySnapshotInto(dst: WorldSnapshot, src: WorldSnapshot): WorldSnapshot {
  dst.t = src.t; dst.matchClock = src.matchClock; dst.half = src.half; dst.phase = src.phase;
  dst.score.home = src.score.home; dst.score.away = src.score.away;
  dst.possession = src.possession;
  cp(dst.ball.pos, src.ball.pos); cp(dst.ball.vel, src.ball.vel);
  dst.ball.spin = src.ball.spin; dst.ball.ownerId = src.ball.ownerId;
  const sp = src.players, dp = dst.players;
  while (dp.length < sp.length) dp.push(emptyPlayer());
  if (dp.length > sp.length) dp.length = sp.length;
  for (let i = 0; i < sp.length; i++) {
    const s = sp[i]!, d = dp[i]!;
    d.id = s.id; d.team = s.team; d.number = s.number;
    cp(d.pos, s.pos); cp(d.vel, s.vel);
    d.heading = s.heading; d.anim = s.anim; d.animPhase = s.animPhase; d.hasBall = s.hasBall;
    if (s.sentOff || d.sentOff) d.sentOff = s.sentOff;
  }
  cp(dst.officials.referee, src.officials.referee);
  cp(dst.officials.assistant1, src.officials.assistant1);
  cp(dst.officials.assistant2, src.officials.assistant2);
  cp(dst.focus, src.focus);
  dst.intensity = src.intensity;
  return dst;
}

const lerpN = (a: number, b: number, t: number): number => a + (b - a) * t;
const lerpInto = (d: Vec3, a: Vec3, b: Vec3, t: number): void => {
  d.x = a.x + (b.x - a.x) * t; d.y = a.y + (b.y - a.y) * t; d.z = a.z + (b.z - a.z) * t;
};
function lerpAngle(a: number, b: number, t: number): number {
  let d = (b - a) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2; else if (d < -Math.PI) d += Math.PI * 2;
  return a + d * t;
}

/** Interpolate continuous fields between a and b into out; discrete fields from the nearer one. */
export function lerpSnapshotInto(out: WorldSnapshot, a: WorldSnapshot, b: WorldSnapshot, alpha: number): WorldSnapshot {
  const near = alpha < 0.5 ? a : b;
  copySnapshotInto(out, near);
  if (a === b || alpha <= 0 || alpha >= 1) return out;
  out.t = lerpN(a.t, b.t, alpha);
  out.matchClock = lerpN(a.matchClock, b.matchClock, alpha);
  out.intensity = lerpN(a.intensity, b.intensity, alpha);
  lerpInto(out.ball.pos, a.ball.pos, b.ball.pos, alpha);
  lerpInto(out.ball.vel, a.ball.vel, b.ball.vel, alpha);
  lerpInto(out.focus, a.focus, b.focus, alpha);
  lerpInto(out.officials.referee, a.officials.referee, b.officials.referee, alpha);
  lerpInto(out.officials.assistant1, a.officials.assistant1, b.officials.assistant1, alpha);
  lerpInto(out.officials.assistant2, a.officials.assistant2, b.officials.assistant2, alpha);
  const n = Math.min(a.players.length, b.players.length, out.players.length);
  for (let i = 0; i < n; i++) {
    const pa = a.players[i]!, pb = b.players[i]!, po = out.players[i]!;
    if (pa.id !== pb.id) continue; // roster changed (sub) — keep nearer frame as-is
    lerpInto(po.pos, pa.pos, pb.pos, alpha);
    lerpInto(po.vel, pa.vel, pb.vel, alpha);
    po.heading = lerpAngle(pa.heading, pb.heading, alpha);
    if (pa.anim === pb.anim) {
      // Phase may wrap (looping anims): interpolate along the shorter direction.
      let d = pb.animPhase - pa.animPhase;
      if (d < -0.5) d += 1; else if (d > 0.5) d -= 1;
      po.animPhase = (((pa.animPhase + d * alpha) % 1) + 1) % 1;
    }
  }
  return out;
}

export function createReplayBuffer(opts: ReplayBufferOptions = {}): ReplayBuffer {
  const hz = opts.hz ?? 30;
  const seconds = opts.seconds ?? 90;
  const capacity = Math.max(2, Math.ceil(seconds * hz) + 1);
  const minDt = (1 / hz) * 0.98;
  const slots: WorldSnapshot[] = [];
  let head = 0; // index of the next write
  let size = 0;
  const result: ReplaySample = { prev: null!, next: null!, alpha: 0 };

  const idx = (i: number): number => (head - size + i + capacity * 2) % capacity;
  const at = (i: number): WorldSnapshot => slots[idx(i)]!;

  const buf: ReplayBuffer = {
    capacity,
    get size() { return size; },
    get oldestT() { return size ? at(0).t : NaN; },
    get newestT() { return size ? at(size - 1).t : NaN; },
    push(s) {
      if (size) {
        const last = at(size - 1).t;
        if (s.t < last) { size = 0; head = 0; } // time went backwards: new match / restart
        else if (s.t - last < minDt) return false;
      }
      let slot = slots[head];
      if (!slot) { slot = createEmptySnapshot(s.players.length); slots[head] = slot; }
      copySnapshotInto(slot, s);
      head = (head + 1) % capacity;
      if (size < capacity) size++;
      return true;
    },
    sample(t) {
      if (!size) return null;
      const first = at(0), last = at(size - 1);
      if (size === 1 || t <= first.t) { result.prev = first; result.next = size > 1 ? at(1) : first; result.alpha = 0; return result; }
      if (t >= last.t) { result.prev = at(size - 2); result.next = last; result.alpha = 1; return result; }
      // Binary search: largest i with at(i).t <= t.
      let lo = 0, hi = size - 1;
      while (hi - lo > 1) {
        const mid = (lo + hi) >> 1;
        if (at(mid).t <= t) lo = mid; else hi = mid;
      }
      const a = at(lo), b = at(hi);
      const span = b.t - a.t;
      result.prev = a; result.next = b;
      result.alpha = span > 0 ? Math.min(1, Math.max(0, (t - a.t) / span)) : 0;
      return result;
    },
    interpolate(t, out) {
      const s = buf.sample(t);
      if (!s) return null;
      return lerpSnapshotInto(out, s.prev, s.next, s.alpha);
    },
    at(i) {
      if (i < 0 || i >= size) throw new RangeError(`replay buffer index ${i} out of range (size ${size})`);
      return at(i);
    },
    clear() { size = 0; head = 0; },
  };
  return buf;
}
